import { BackendError, Service } from '@genial/services';
import { GetOfferDocumentsSuccessListResponse, MetaResponse } from '@genial/services/dist/models';
import { selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { transformSortItem } from '../../../utils/transformSortItem';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import {
  normalizeFundOfferDetail,
  normalizeFundsFilters,
  normalizeFundsOffers,
} from '../../product/normalizer';
import {
  DynamicCustomFiltersProps,
  FundOfferDetailEntityNormalized,
  FundOfferEntityNormalized,
} from '../../product/types';
import { fixOrderFieldFunds, screenName } from '../constants';
import { DynamicProductQueryParams } from '../types';

export const fundsAvailableProductsQuery = selectorFamily<
  { data: FundOfferEntityNormalized[]; meta: MetaResponse },
  DynamicProductQueryParams
>({
  key: 'AvailableProducts/fundsAvailableProductsQuery',
  get: ({ filtersToApply, searchTerm, page, orderBy }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);
        const sortItems = transformSortItem(orderBy);
        const sortItemsForApi =
          sortItems?.sort != null
            ? [
                {
                  sort: fixOrderFieldFunds(sortItems.sort),
                  sortDirection: sortItems.sortDirection,
                },
              ]
            : undefined;
        const { data, meta } = await Service.Fund.V1.getAllOffersBasket({
          ...filtersToApply,
          ...(searchTerm ? { searchTerm } : {}),
          ...(sortItemsForApi ? { sortItems: sortItemsForApi } : {}),
          isExclusive: false,
          page,
          limit: 10,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = normalizeFundsOffers(data);

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const fundsAvailableProductsFiltersQuery = selectorFamily<
  DynamicCustomFiltersProps,
  undefined
>({
  key: 'AvailableProducts/fundsAvailableProductsFiltersQuery',
  get: () => {
    return async () => {
      const { data } = await Service.Fund.V1.getOffersCategoriesAdvisors({
        screenName,
      });

      const normalizedData = normalizeFundsFilters(data);

      return normalizedData;
    };
  },
});

export const fundsAvailableProductsDetailsQuery = selectorFamily<
  { data: FundOfferDetailEntityNormalized } | null | undefined,
  { fundId: string; fundCnpj: string; accountNumber: string; screen?: string }
>({
  key: 'NewApplication/fundDetailsQuery',
  get: ({ fundId, fundCnpj }) => {
    return async () => {
      if (!fundId || !fundCnpj) {
        return;
      }

      const { data } = await Service.Fund.V1.getOffersDetails({
        fundCnpj,
        productId: fundId,
        checkingAccountNumber: '',
        screenName,
      });

      const normalizedData = normalizeFundOfferDetail(data);

      return { data: normalizedData };
    };
  },
});

export const fundsOfferDocumentsQuery = selectorFamily<
  GetOfferDocumentsSuccessListResponse | null | undefined,
  { internalProductId: string }
>({
  key: 'AvailableProducts/fundsOfferDocumentsQuery',
  get: ({ internalProductId }) => {
    return async ({ get }) => {
      if (!internalProductId) {
        return;
      }

      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const data = await Service.Fund.V1.getOffersByDocumentsInternalProductId({
        internalProductId,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
        screenName,
      });

      return data;
    };
  },
});
